import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchReprocessingInvoicesCounts } from './fetchReprocessingInvoicesCounts';
import {
  type GetReprocessingInvoicesCountsPayload,
  type ReprocessingInvoicesCountsResponse,
} from './types';
import { zodReprocessingInvoicesCountsPayloadSchema } from './validations';

/**
 * Generates the query options for the reprocessing invoice counts query.
 */
export function getReprocessingInvoicesCountsQueryOptions({
  options = {},
  queryParameters,
}: {
  options?: Omit<
    UseQueryOptions<ReprocessingInvoicesCountsResponse>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: Partial<GetReprocessingInvoicesCountsPayload>;
}): UseQueryOptions<ReprocessingInvoicesCountsResponse> {
  const validatedSearchParams =
    zodReprocessingInvoicesCountsPayloadSchema.parse(queryParameters);

  return {
    queryFn: async () => fetchReprocessingInvoicesCounts(validatedSearchParams),
    queryKey: queryKeysInvoiceReprocessing.getProcessCounts(
      validatedSearchParams,
    ),
    staleTime: ms('30s'),
    ...options,
  };
}

/**
 * React Query based custom hook for getting aggregated invoice process counts with given query parameters.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_aggregated_invoice_processes_invoice_process_aggregated_get
 */
export function useQueryReprocessingInvoicesCounts(
  queryParameters: Partial<GetReprocessingInvoicesCountsPayload>,
  options?: Omit<
    UseQueryOptions<ReprocessingInvoicesCountsResponse>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ReprocessingInvoicesCountsResponse>({
    ...getReprocessingInvoicesCountsQueryOptions({
      options,
      queryParameters,
    }),
  });
}
